import { useFieldArray, useForm } from "react-hook-form";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { EntityState } from "../../../Common/api/models/EntityState";
import _ from "lodash";
import { 
  manageCustomGroupMembers,
  ManageCustomGroupMembersCommand 
} from "../api/customGroupsHttpClient";
import { User } from "../../../Common/models/FormModels";

export interface CustomGroupUsersFormType {
  users: User[];
}

const useCustomGroupUsersForm = (
  projectMembers: User[],
  projectId: string,
  toolId: string,
  groupName: string,
  toolProjectId = ""
) => {
  const [isSaving, setIsSaving] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  
  const getFormUsers = (users: User[]) => {
    return users.map((u) => ({
      ...u,
      editStatus: EntityState.NotChanged,
    }));
  };

  const defaultValues: CustomGroupUsersFormType = {
    users: getFormUsers(projectMembers),
  };

  const form = useForm<CustomGroupUsersFormType>({
    defaultValues: defaultValues,
    mode: "onBlur",
  });
  
  const {
    control,
    watch,
    setValue,
    getValues,
    formState: { isValid },
  } = form;
  
  watch("users");

  // Custom isDirty logic similar to useProjectUsersForm
  const checkIfDirty = (): boolean => {
    const currentUsers = getValues("users");
    const initialUsers = projectMembers;
    
    if (!initialUsers) {
      return false;
    }

    // Check if there are any changes to be saved (users to add or remove from group)
    let hasChanges = false;
    
    for (const user of currentUsers) {
      const initialUser = initialUsers.find(iu => iu.memberId === user.memberId);
      if (initialUser) {
        // User was initially not in group, now is in group -> Change detected
        if (!initialUser.isInCustomGroup && user.isInCustomGroup) {
          hasChanges = true;
          break;
        }
        // User was initially in group, now is not in group -> Change detected  
        else if (initialUser.isInCustomGroup && !user.isInCustomGroup) {
          hasChanges = true;
          break;
        }
      }
    }

    return hasChanges;
  };

  const { append, remove } = useFieldArray({
    control,
    name: "users",
  });

  const handleCustomGroupUsersSubmit = async () => {
    const users: User[] = form.getValues("users");
    if (!isValid) {
      enqueueSnackbar("Please correct the validation errors", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      return false;
    }

    const initialUsers = projectMembers;
    
    // Update editStatus based on changes (similar to useProjectUsersForm)
    if (initialUsers) {
      for (const user of users) {
        const formUser = user;
        if (
          formUser.editStatus !== EntityState.NotChanged &&
          formUser.editStatus !== EntityState.Update
        ) {
          continue;
        }

        const initialUser = initialUsers.find(
          (iu) => iu.memberId === formUser.memberId
        );
        if (initialUser) {
          if (
            !_.isEqual(
              _.omit(initialUser, "editStatus"),
              _.omit(formUser, "editStatus")
            )
          ) {
            formUser.editStatus = EntityState.Update;
          } else {
            formUser.editStatus = EntityState.NotChanged;
          }
        }
      }
    }

    // Collect users to add and remove
    const usersToAdd: User[] = [];
    const usersToDelete: User[] = [];
    
    if (initialUsers) {
      for (const user of users) {
        const initialUser = initialUsers.find(iu => iu.memberId === user.memberId);
        if (initialUser) {
          // User was initially not in group, now is in group -> Add
          if (!initialUser.isInCustomGroup && user.isInCustomGroup) {
            usersToAdd.push(user);
          }
          // User was initially in group, now is not in group -> Delete
          else if (initialUser.isInCustomGroup && !user.isInCustomGroup) {
            usersToDelete.push(user);
          }
          // If back to original state, do nothing (not in add or delete arrays)
        }
      }
    }

    // If no changes, return success
    if (usersToAdd.length === 0 && usersToDelete.length === 0) {
      enqueueSnackbar("No changes to save", {
        variant: "info",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });
      return true;
    }

    setIsSaving(true);

    try {
      const command: ManageCustomGroupMembersCommand = {
        ProjectId: projectId,
        ToolId: toolId,
        ToolProjectId: toolProjectId,
        GroupName: groupName,
        Add: usersToAdd,
        Delete: usersToDelete,
      };

      await manageCustomGroupMembers(command);

      // Reset form with updated values
      form.reset({
        users: getFormUsers(users.map(u => ({ ...u, editStatus: EntityState.NotChanged }))),
      } as CustomGroupUsersFormType);

      enqueueSnackbar("Custom group users have been updated!", {
        variant: "success",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });

      return true;
    } catch (error) {
      let errorMsg = "";
      if (error instanceof Error) {
        errorMsg = error.message;
      } else {
        errorMsg = "Custom group users have not been updated!";
      }
      enqueueSnackbar(errorMsg, {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "center" },
      });

      console.error("Something went wrong", error);
      return false;
    } finally {
      setIsSaving(false);
    }
  };


  return {
    form,
    append,
    handleCustomGroupUsersSubmit,
    isSaving,
    checkIfDirty,
  };
};

export default useCustomGroupUsersForm;